<template>
  <ResizeObserver
    :breakpoints="breakpoints"
    data-sb-regression="sb-masonry"
    @on-resize="setCurrentEl"
  >
    <template #default="{ el }">
      <div
        ref="sb-masonry"
        class="sb-masonry"
        :class="el.is"
      >
        <!--
          @slot text, components, or HtmlElements as children of the component
        -->
        <slot
          name="sb-masonry-container"
          :current-size="currentEl"
        >
          <slot />
        </slot>
      </div>
    </template>
  </resizeobserver>
</template>

<script>
import ResizeObserver from '@src/components/ResizeObserver/ResizeObserver.vue';
import { Packer, Block } from '@src/components/Masonry/utils/packer';

const rowSize = 50;

export default {
  name: 'SbMasonry',
  components: { ResizeObserver },
  provide() {
    return {
      sbMasonry: {
        items: this.items,
      },
    };
  },
  props: {
    /**
    * Breakpoints which define the Masonry Layout
    */
    breakpoints: {
      type: Object,
      required: true,
    },
    /**
    * Orientation of the layout
    */
    orientation: {
      type: String,
      default: 'column',
    },
    /**
    * Total number of columns into which the layout is broken up
    */
    span: {
      type: Number,
      required: true,
    },
    /**
    * Gap between the items
    */
    gap: {
      type: String,
      default: '1rem',
    },
  },
  data() {
    return {
      currentEl: null,
      items: [],
    };
  },
  methods: {
    setCurrentEl(el) {
      const currentEl = Object.keys(el).find((e) => el[e]);
      if (currentEl && currentEl !== this.currentEl) {
        this.currentEl = currentEl;
        this.fitBlocks();
      }
    },
    fitBlocks() {
      if (!this.currentEl) return;

      //* *****1. Reset grid items******
      const masonry = this.$refs['sb-masonry'];
      masonry.style.gridTemplateColumns = `repeat(${this.span}, 1fr)`;
      masonry.style.gridGap = this.gap;
      this.items.forEach((item) => {
        const { configurations, el } = item;
        const { span } = configurations[this.currentEl];
        el.style.gridColumn = `1 / span ${span}`;
        el.style.gridRow = '';
      });

      //* *****2. Compute dimensions******
      const mappedConfigurations = this.items.map((item) => {
        const { configurations, el } = item;
        const { priority, span } = configurations[this.currentEl];
        return {
          priority,
          w: span,
          h: this.span === span ? 1 : Math.ceil(el.offsetHeight / rowSize),
          el,
        };
      });

      //* *****3. Sort based on priority******
      mappedConfigurations.sort((a, b) => a.priority - b.priority);

      //* *****4. Pack******
      const blocks = mappedConfigurations.map((c) => new Block(c.w, c.h));
      const packer = new Packer(this.span);
      packer.fit(blocks);

      //* *****5. Draw*******
      blocks.forEach((b, i) => {
        const { el } = mappedConfigurations[i];
        el.style.gridColumn = `${b.fit.x} / span ${b.w}`;
        el.style.gridRow = `${b.fit.y} / span ${b.h}`;
      });
    },
  },
};
</script>
<style lang="scss">
@import '@src/components/Masonry/style/sb-masonry.scss';
</style>
